'use client'

import InputWrapper from '@trustoo/components/input/InputWrapper'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, FocusEvent, forwardRef, ReactNode, useState } from 'react'

export interface TextAreaFieldProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'ref'> {
    error?: string | boolean
    defaultErrorMsg?: string
    legend?: string
    animatedPlaceholder?: ReactNode
    labelClassName?: string
    textAreaClassName?: string
    errorClassName?: string
    withErrorIcon?: boolean
}

const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
    (
        {
            value,
            error,
            defaultErrorMsg,
            legend,
            placeholder,
            animatedPlaceholder,
            autoFocus,
            onBlur: onBlurProp,
            onFocus: onFocusProp,
            className,
            labelClassName,
            textAreaClassName = 'min-h-[105px]',
            errorClassName,
            withErrorIcon = false,
            ...restTextAreaProps
        },
        ref,
    ) => {
        const [focused, setFocused] = useState(false)

        const onFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
            if (restTextAreaProps.readOnly) {
                const activeElement = document.activeElement as HTMLElement | null
                if (activeElement && typeof activeElement.blur === 'function') {
                    activeElement.blur()
                }
            } else {
                setFocused(true)
            }
            onFocusProp?.(e)
        }

        const onBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
            setFocused(false)
            onBlurProp?.(e)
        }

        const hasValue = value !== '' && value !== undefined && value !== null

        return (
            <InputWrapper
                id={restTextAreaProps.id}
                hasValue={hasValue}
                error={error}
                isFocused={focused}
                isDisabled={restTextAreaProps.disabled}
                legend={legend}
                animatedPlaceholder={animatedPlaceholder}
                className={className}
                labelClassName={labelClassName}
                errorClassName={errorClassName}
                defaultErrorMsg={defaultErrorMsg}
                withErrorIcon={withErrorIcon}>
                <textarea
                    {...restTextAreaProps}
                    ref={ref}
                    value={value}
                    autoFocus={autoFocus}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    className={clsx(
                        'flex-1 h-full px-3.5 py-3 resize-y',
                        'text-dark leading-4 border-none rounded box-border',
                        'transition-[border] duration-[140ms] ease-in-out',
                        'overflow-hidden text-ellipsis',
                        'outline-none',
                        'disabled:cursor-not-allowed disabled:bg-[inherit] disabled:text-text-secondary',
                        'read-only:text-text-secondary',
                        focused ? 'placeholder:text-[#b6b9cd]' : 'placeholder:text-[#80868b]',
                        textAreaClassName,
                    )}
                />
            </InputWrapper>
        )
    },
)

TextAreaField.displayName = 'TextAreaField'

export default TextAreaField
